import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Icon from '../AppIcon';
import BusinessSetup from './BusinessSetup';
import PlanSelectionStep from './PlanSelectionStep';
import ServiceSetup from './ServiceSetup';
import CustomerImport from './CustomerImport';
import CampaignSetup from './CampaignSetup';
// InteractiveTutorial component skipped for now

const OnboardingWizard = () => {
  const { userProfile, updateProfile, signOut } = useAuth();
  const { currentOrganization, updateOrganization } = useOrganization();
  const navigate = useNavigate();
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [onboardingData, setOnboardingData] = useState({
    businessSetup: {},
    planSelection: {},
    serviceSetup: {},
    campaignSetup: {},
    customerImport: {},
    tutorialComplete: false
  });

  // Load existing onboarding data if available
  useEffect(() => {
    if (userProfile?.onboarding_data) {
      setOnboardingData(userProfile.onboarding_data);
      setCurrentStep(userProfile.onboarding_step || 0);
    }
  }, [userProfile]);

  const steps = [
    {
      id: 'welcome',
      title: 'Welcome to ServiceReviewAI!',
      subtitle: 'Let\'s set up your review management system',
      icon: 'Sparkles'
    },
    {
      id: 'business',
      title: 'Business Setup',
      subtitle: 'Tell us about your business',
      icon: 'Building'
    },
    {
      id: 'plan',
      title: 'Choose Your Plan',
      subtitle: 'Select the plan that fits your needs',
      icon: 'CreditCard'
    },
    {
      id: 'services',
      title: 'Service Configuration',
      subtitle: 'What services do you offer?',
      icon: 'Wrench'
    },
    {
      id: 'campaign',
      title: 'Basic Campaign Setup',
      subtitle: 'Configure your review emails',
      icon: 'Mail'
    },
    {
      id: 'customers',
      title: 'First Customer',
      subtitle: 'Add your first customer (optional)',
      icon: 'UserPlus'
    },
    {
      id: 'tutorial',
      title: 'Setup Complete',
      subtitle: 'Ready to start using ServiceReviewAI',
      icon: 'CheckCircle'
    }
  ];

  const saveProgress = async (stepData, nextStep = null) => {
    try {
      setIsLoading(true);
      
      const updatedData = {
        ...onboardingData,
        ...stepData
      };
      
      const step = nextStep !== null ? nextStep : currentStep;
      
      // Update user profile with progress
      await updateProfile({
        onboarding_data: updatedData,
        onboarding_step: step,
        onboarding_completed: step >= steps.length - 1
      });
      
      setOnboardingData(updatedData);
      
      if (nextStep !== null) {
        setCurrentStep(nextStep);
      }
      
    } catch (error) {
      console.error('Error saving onboarding progress:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = async (stepData = {}) => {
    if (currentStep < steps.length - 1) {
      await saveProgress(stepData, currentStep + 1);
    } else {
      await completeOnboarding(stepData);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = async () => {
    await handleNext();
  };

  const completeOnboarding = async (finalData = {}) => {
    try {
      setIsLoading(true);
      
      const completedData = {
        ...onboardingData,
        ...finalData,
        tutorialComplete: true
      };
      
      // Mark onboarding as completed
      await updateProfile({
        onboarding_data: completedData,
        onboarding_step: steps.length,
        onboarding_completed: true
      });
      
      // Navigate to dashboard
      navigate('/dashboard');
      
    } catch (error) {
      console.error('Error completing onboarding:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const renderStepContent = () => {
    switch (steps[currentStep].id) {
      case 'welcome':
        return (
          <div className="text-center space-y-6 max-w-md mx-auto">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto">
              <Icon name="Sparkles" size={32} color="white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-text-primary mb-2">
                Welcome to ServiceReviewAI!
              </h1>
              <p className="text-text-secondary text-lg">
                Let's get your AI-powered review management system set up in just a few minutes.
              </p>
            </div>
            <div className="bg-primary-50 rounded-lg p-4">
              <p className="text-sm text-text-secondary">
                We'll help you configure your business details, choose your plan, set up your services, 
                and show you how to get the most out of ServiceReviewAI.
              </p>
            </div>
          </div>
        );
      
      case 'business':
        return (
          <BusinessSetup
            data={onboardingData.businessSetup}
            onSave={(data) => handleNext({ businessSetup: data })}
            onBack={handleBack}
            isLoading={isLoading}
          />
        );
      
      case 'plan':
        return (
          <PlanSelectionStep
            initialData={onboardingData.planSelection}
            onNext={() => handleNext()}
            onBack={handleBack}
            onSaveData={(data) => saveProgress(data)}
          />
        );
      
      case 'services':
        return (
          <ServiceSetup
            data={onboardingData.serviceSetup}
            businessSetup={onboardingData.businessSetup}
            onSave={(data) => handleNext({ serviceSetup: data })}
            onSkip={handleSkip}
            onBack={handleBack}
            isLoading={isLoading}
          />
        );

      case 'campaign':
        return (
          <CampaignSetup
            data={onboardingData.campaignSetup}
            onSave={(data) => handleNext({ campaignSetup: data })}
            onSkip={handleSkip}
            onBack={handleBack}
            isLoading={isLoading}
          />
        );
      
      case 'customers':
        return (
          <CustomerImport
            data={onboardingData.customerImport}
            onSave={(data) => handleNext({ customerImport: data })}
            onSkip={handleSkip}
            onBack={handleBack}
            isLoading={isLoading}
          />
        );
      
      case 'tutorial':
        return (
          <div className="text-center space-y-6 max-w-md mx-auto">
            <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center mx-auto">
              <Icon name="CheckCircle" size={32} color="white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-text-primary mb-2">
                Setup Complete!
              </h1>
              <p className="text-text-secondary text-lg">
                Your ServiceReviewAI account is ready to use.
              </p>
            </div>
            <div className="bg-success-50 rounded-lg p-4">
              <p className="text-sm text-text-secondary mb-4">
                You can now start managing your reviews with AI-powered insights. 
                Explore the dashboard to get started!
              </p>
              <Button
                onClick={() => completeOnboarding()}
                disabled={isLoading}
                className="w-full"
              >
                {isLoading ? 'Completing...' : 'Go to Dashboard'}
                <Icon name="ArrowRight" size={16} className="ml-2" />
              </Button>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Progress Bar */}
      <div className="bg-surface border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Wrench" size={16} color="white" />
              </div>
              <div>
                <h2 className="font-semibold text-text-primary">ServiceReviewAI</h2>
                <p className="text-xs text-text-muted">Setup Wizard</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-text-secondary">
                Step {currentStep + 1} of {steps.length}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignOut}
                className="text-text-muted hover:text-text-secondary text-xs"
              >
                <Icon name="LogOut" size={14} className="mr-1" />
                Sign Out
              </Button>
            </div>
          </div>
          
          {/* Progress indicator */}
          <div className="flex items-center space-x-2">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`flex-1 h-2 rounded-full transition-colors ${
                  index <= currentStep ? 'bg-primary' : 'bg-surface-hover'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-surface rounded-lg border border-border p-8">
          {/* Step Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Icon name={steps[currentStep].icon} size={20} className="text-primary" />
              <h1 className="text-xl font-semibold text-text-primary">
                {steps[currentStep].title}
              </h1>
            </div>
            <p className="text-text-secondary">
              {steps[currentStep].subtitle}
            </p>
          </div>

          {/* Step Content */}
          <div className="mb-8">
            {renderStepContent()}
          </div>

          {/* Navigation */}
          {steps[currentStep].id === 'welcome' && (
            <div className="flex justify-center">
              <Button
                onClick={() => handleNext()}
                disabled={isLoading}
                className="px-8"
              >
                Get Started
                <Icon name="ArrowRight" size={16} className="ml-2" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard; 